/**
 * The bonds recorded on a case, the court level they imply, and the failure-to-appear status
 * (Lloyd, Aug 21 / Aug 24 2026).
 *
 * Two facts decide the failure-to-appear fee: the **court level** ($350 in JP/municipal, $1,500 in
 * a court of record) and whether there **is** a failure to appear at all. The first follows a filed
 * appeal bond; the second is derived from the recorded appearance outcomes.
 *
 * `AppealBondPanel` GENERATES a bond. This records one that already exists — bonds predating the
 * feature live only as PDFs in mailboxes and there is no backfill.
 */

export type BondType = 'APPEAL' | 'ATTORNEY';

export const BOND_TYPE_LABELS: Record<BondType, string> = {
  APPEAL: 'Appeal bond (moves the case to a court of record)',
  ATTORNEY: "Attorney bond (secures the client's appearance)",
};

export type CourtLevel = 'JP_OR_MUNICIPAL' | 'COURT_OF_RECORD';

export const COURT_LEVEL_LABELS: Record<CourtLevel, string> = {
  JP_OR_MUNICIPAL: 'Justice or municipal court',
  COURT_OF_RECORD: 'County, county court at law or district court',
};

export interface CourtLevelExplanation {
  level: CourtLevel;
  reason: string;
  hasUnfiledAppealBond: boolean;
}

export interface FtaStatus {
  hasFailureToAppear: boolean;
  failureCount: number;
  mostRecentFailureAt: string | null;
  label: string;
}

export interface CaseBondDto {
  CaseBond_guid: string;
  CaseBond_Type: string;
  CaseBond_FromCourtType: string | null;
  CaseBond_AmountCents: number | null;
  CaseBond_JudgmentDate: string | null;
  CaseBond_GeneratedAt: string;
  CaseBond_GeneratedBy: string | null;
  CaseBond_SignedAt: string | null;
  CaseBond_FiledAt: string | null;
  CaseBond_Note: string | null;
}

export interface CaseBondsResponse {
  bonds: CaseBondDto[];
  courtLevel: CourtLevelExplanation;
  ftaStatus: FtaStatus;
}

export interface RecordBondInput {
  CaseBond_Type: BondType;
  CaseBond_FromCourtType?: 'municipal' | 'jp' | null;
  CaseBond_AmountCents?: number | null;
  CaseBond_JudgmentDate?: string | null;
  CaseBond_SignedAt?: string | null;
  CaseBond_FiledAt?: string | null;
  CaseBond_Note?: string | null;
}

export interface UpdateBondDatesInput {
  CaseBond_SignedAt?: string | null;
  CaseBond_FiledAt?: string | null;
  CaseBond_AmountCents?: number | null;
  CaseBond_Note?: string | null;
}

export default class CaseBondApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async list(ticketGuid: string): Promise<CaseBondsResponse> {
    const response = await fetch(
      `${CaseBondApi.host()}/api/v1/manager/ticket/${ticketGuid}/bonds`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load the bonds on this case');
    return response.json();
  }

  static async record(ticketGuid: string, input: RecordBondInput): Promise<CaseBondDto> {
    const response = await fetch(
      `${CaseBondApi.host()}/api/v1/manager/ticket/${ticketGuid}/bonds`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to record the bond');
    }
    return response.json();
  }

  static async updateDates(
    bondGuid: string,
    input: UpdateBondDatesInput,
  ): Promise<CaseBondDto> {
    const response = await fetch(`${CaseBondApi.host()}/api/v1/manager/bond/${bondGuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to update the bond');
    }
    return response.json();
  }
}
